import React, { useContext, useState } from "react";
import { DrawerHistoryContext } from "../contexts";
import { usePlan2PlateDisclosure } from "./Plan2PlateDrawer";
import { EditDishDrawer } from "./EditDishDrawer";
import { EditFoodDrawer } from "./EditFoodDrawer";
import { EditPurchaseDrawer } from "./EditPurchaseDrawer";
import { loadDishModification } from "../loaders";
import { getPurchase, loadFood } from "../api";
import { DishModification } from "../data/dishModification";
import Food from "../data/food";
import { Purchase } from "../data/purchase";

export type EntityEditTarget =
  | { kind: "dish"; id: number }
  | { kind: "food"; id: number }
  | { kind: "purchase"; id: number };

/**
 * Gemeinsame Stift-Aktion für Gericht / Lebensmittel / Kaufprodukt. Lädt die
 * Entität und öffnet den passenden Bearbeiten-Drawer. Wird u. a. im Verzehr
 * genutzt, damit dort derselbe Stift wie im Plan-Dialog funktioniert.
 */
export function useEntityEditDrawers(onSaved?: () => void) {
  const drawerHistoryContext = useContext(DrawerHistoryContext);
  const dishDisclosure = usePlan2PlateDisclosure(
    drawerHistoryContext,
    "edit-dish",
  );
  const foodDisclosure = usePlan2PlateDisclosure(
    drawerHistoryContext,
    "edit-food",
  );
  const purchaseDisclosure = usePlan2PlateDisclosure(
    drawerHistoryContext,
    "edit-purchase",
  );

  const [dish, setDish] = useState<DishModification | undefined>();
  const [food, setFood] = useState<Food | undefined>();
  const [purchase, setPurchase] = useState<Purchase | undefined>();

  const open = async (target: EntityEditTarget) => {
    if (target.kind === "dish") {
      setDish(await loadDishModification(target.id));
      dishDisclosure.onOpen();
    } else if (target.kind === "food") {
      const loaded = (await loadFood({ path: { foodId: String(target.id) } }))
        .data;
      setFood(loaded as Food);
      foodDisclosure.onOpen();
    } else {
      const loadedPurchase = (
        await getPurchase({ path: { purchaseId: String(target.id) } })
      ).data;
      const loadedFood = (
        await loadFood({ path: { foodId: String(loadedPurchase.foodId) } })
      ).data;
      // "Aus Gericht"-Kaufprodukte sind nicht direkt bearbeitbar → Food-Editor öffnen.
      if ((loadedFood as Food).fromDish) {
        setFood(loadedFood as Food);
        foodDisclosure.onOpen();
        return;
      }
      setPurchase(loadedPurchase);
      setFood(loadedFood as Food);
      purchaseDisclosure.onOpen();
    }
  };

  const element = (
    <>
      <EditDishDrawer
        dish={dish}
        open={dishDisclosure.isOpen}
        onClose={dishDisclosure.onClose}
        onSubmit={() => {
          onSaved?.();
        }}
      />
      <EditFoodDrawer
        allowUnitDeletion
        food={food}
        open={foodDisclosure.isOpen}
        onClose={foodDisclosure.onClose}
        onSubmit={() => {
          onSaved?.();
        }}
      />
      <EditPurchaseDrawer
        purchase={purchase}
        food={food}
        open={purchaseDisclosure.isOpen}
        onClose={purchaseDisclosure.onClose}
        onSubmit={() => {
          onSaved?.();
        }}
      />
    </>
  );

  return { open, element };
}
